import { atom, useAtom, useAtomValue } from 'jotai';
import { LENDERS_IDS } from '../constants';
import { secureHttpClient } from '../restClient';
import { appDataByMerchant } from './configManager';
import { StoreId } from './storeManager';
import { atomFamily } from 'jotai/utils';
import logger from './loggerManager';
import { environment } from '../environment';

const paths = {
  byLenderSettleReferenceId: 'transactionsData.lenderSettleReferenceId',
  byLenderAuthorizeReferenceId: 'transactionsData.lenderAuthorizeReferenceId',
};

export const lendersConfig = {
  [LENDERS_IDS.acima]: paths.byLenderSettleReferenceId,
  [LENDERS_IDS.aff]: paths.byLenderSettleReferenceId,
  [LENDERS_IDS.ally]: paths.byLenderSettleReferenceId,
  [LENDERS_IDS.gaf]: paths.byLenderSettleReferenceId,
  [LENDERS_IDS.genesis]: paths.byLenderSettleReferenceId,
  [LENDERS_IDS.lendingPoint]: paths.byLenderSettleReferenceId,
  [LENDERS_IDS.milan]: paths.byLenderSettleReferenceId,
  [LENDERS_IDS.wellsFargo]: paths.byLenderAuthorizeReferenceId,
  [LENDERS_IDS.coveredCare]: paths.byLenderSettleReferenceId,
  [LENDERS_IDS.patientFi]: paths.byLenderSettleReferenceId,
};

export const getDisplayedReferenceId = (object: any, lenderId: string) => {
  if (!object || !lenderId) return;
  const path = lendersConfig[lenderId] ?? paths.byLenderSettleReferenceId;
  return path.split('.').reduce((res, prop) => res[prop], object);
};

export type LenderProductType =
  | 'RevolvingLineOfCredit'
  | 'LeaseToOwnProgram'
  | 'InstallmentProgram'
  | 'LineOfCredit'
  | 'FinancialProgram';

export type ConfigurationResponse = {
  paymentOptions: {
    id: string;
    paymentType: 'Financing' | 'MonthlyInstallments' | 'BiWeeklyPayments';
    lendersIds: string[];
    directLendersIds: string[];
  }[];
  lendersConfigurations: {
    id: string;
    name: string;
    isPrequalificationEnabled: boolean;
    collectBuyerDetails: boolean;
    lenderProductTypes: LenderProductType[];
    logo: string;
    maximalCartValue?: number;
    minimalCartValue?: number;
    configuration: {
      requiredFieldCategories: RequiredFieldCategoryType[];
      doesNotSupportOtb?: boolean;
      allowCheckout?: boolean;
      allowExpressCheckout?: boolean;
    };
  }[];
  branding: {
    merchantId: string;
    logo: string;
    name: string;
  };
  configurationSessionId: string;
};

export enum RequiredFieldCategoryType {
  IdVerification = 'IdVerification',
  MonthlyIncome = 'MonthlyIncome',
}

export type FlowType = 'Apply' | 'Checkout';

const CoreConfigurationLastUpdate = atom<Date>(new Date(Date.now()));

const checkTimePassed = (time: Date) => {
  const INTERVAL_MINUTES = 30;
  var past = new Date(time).getTime();
  var interval = 1000 * 60 * INTERVAL_MINUTES;
  var isPast = new Date().getTime() - past < interval ? false : true;
  return isPast;
};

export const useUpdateConfigurationHandler = () => {
  const [lastUpdate, setLastUpdate] = useAtom(CoreConfigurationLastUpdate);

  return (forceUpdate?: boolean) => {
    //TODO: remove this log when incident #34555 resolved
    logger.log('Checking lenders configuration last update', {
      filename: 'lendersConfigurationManager.ts',
      function: 'useUpdateConfigurationHandler',
      forceUpdate,
    });

    if (forceUpdate || !lastUpdate || checkTimePassed(lastUpdate)) {
      //TODO: remove this log when incident #34555 resolved
      logger.log(
        'Refreshing lastUpdate time to now - will trigger configuration call',
        {
          filename: 'lendersConfigurationManager.ts',
          function: 'useUpdateConfigurationHandler',
          forceUpdate,
        },
      );

      setLastUpdate(new Date(Date.now()));
    }
  };
};

type LenderSettings = {
  options?: {
    allowCheckout: boolean;
    allowExpressCheckout: boolean;
  };
};
const LenderSettingsAtom = atomFamily((lenderId) => {
  return atom(async () => {
    if (!lenderId) return;
    const response = await secureHttpClient.getOperate<LenderSettings>(
      {
        fetchingFunction: 'LenderSettingsAtom',
        fileName: 'lendersConfigurationManager.ts',
      },
      `api/configuration/integration/lender/${lenderId}`,
    );
    return response;
  });
});

const CoreConfiguration = atomFamily((flowType) =>
  atom(async (get) => {
    const storeId = await get(StoreId);
    const publicKey = (await get(appDataByMerchant)).peerPublicKey;
    const refresher = get(CoreConfigurationLastUpdate);
    const merchantData = await get(appDataByMerchant);

    if (!storeId || !publicKey) return;
    try {
      const payload = {
        merchantDetails: {
          storeId,
        },
        checkoutDetails: {
          flowType,
          channel: 'IN_STORE',
          source: 'Sdk',
          currency: merchantData.currency ?? 'USD',
        },
      };

      //TODO: remove this log when incident #34555 resolved
      logger.log('about to call /v3/configuration', {
        filename: 'lendersConfigurationManager.ts',
        function: 'CoreConfiguration atom',
        payload,
        publicKey,
      });

      const response = await fetch(
        `${
          environment.backendBaseUrl
        }/v3/configuration?t=${refresher.getTime()}`,
        {
          headers: {
            Authorization: `Bearer ${publicKey}`,
            'Content-Type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify(payload),
        },
      );
      const configuration: ConfigurationResponse = await response.json();

      if (
        !configuration.configurationSessionId ||
        !configuration.lendersConfigurations ||
        !configuration.lendersConfigurations.length
      )
        return;

      const [firstLender] = configuration.lendersConfigurations;
      let updatedLendersConfig = configuration.lendersConfigurations;
      if (firstLender.id) {
        const settings = await get(LenderSettingsAtom(firstLender.id));
        firstLender.configuration.allowCheckout =
          settings?.options?.allowCheckout;
        firstLender.configuration.allowExpressCheckout =
          settings?.options?.allowExpressCheckout;

        updatedLendersConfig = [
          firstLender,
          ...configuration.lendersConfigurations.slice(1),
        ];
      }

      return {
        ...configuration,
        lendersConfigurations: updatedLendersConfig,
      };
    } catch (e) {
      return;
    }
  }),
);

export const OtbSupportForWaterfall = atom(async (get) => {
  const applyFlowConfig = await get(CoreConfiguration('Apply'));
  const checkoutFlowConfig = await get(CoreConfiguration('Checkout'));
  const applyLendersConfig = applyFlowConfig?.lendersConfigurations;
  const checkoutLendersConfig = checkoutFlowConfig?.lendersConfigurations;

  const anyLenderSupports =
    applyLendersConfig?.find(
      (lender) => !lender.configuration?.doesNotSupportOtb,
    ) ||
    checkoutLendersConfig?.find(
      (lender) => !lender.configuration?.doesNotSupportOtb,
    );
  return !!anyLenderSupports;
});

export const useGetApplyConfiguration = () =>
  useAtomValue(CoreConfiguration('Apply'));

export const useGetCheckoutConfiguration = () =>
  useAtomValue(CoreConfiguration('Checkout'));

const useGetLendersConfig = (flowType: FlowType) => {
  return useAtomValue(CoreConfiguration(flowType))?.lendersConfigurations;
};

export const useGetIdVerificationRequired = (flowType: FlowType) => {
  const lendersConfig = useGetLendersConfig(flowType);
  const idVerificationRequired = lendersConfig?.find((lender) =>
    lender.configuration?.requiredFieldCategories?.find(
      (c) => c === RequiredFieldCategoryType.IdVerification,
    ),
  );
  return !!idVerificationRequired;
};

export const useGetSelectedLenderDoesNotSupportOtb = (lenderId: string) => {
  const applyLendersConfig = useGetLendersConfig('Apply');
  const checkoutLendersConfig = useGetLendersConfig('Checkout');
  const selectedLenderApplyConfig = applyLendersConfig?.find(
    (l) => l.id === lenderId,
  )?.configuration;
  const selectedLenderCheckoutConfig = checkoutLendersConfig?.find(
    (l) => l.id === lenderId,
  )?.configuration;

  return (
    selectedLenderApplyConfig?.doesNotSupportOtb ||
    selectedLenderCheckoutConfig?.doesNotSupportOtb
  );
};

export const useGetLenderSettings = (lenderId: string) =>
  useAtomValue(LenderSettingsAtom(lenderId));
